import { useEffect, useMemo, useState } from 'react';
import { collection, onSnapshot, query, orderBy, doc, updateDoc, deleteDoc } from 'firebase/firestore';
import { X, Mail, Check, Phone, Clock, Save, Trash2 } from 'lucide-react';
import { db, sendGraceHavenEmail } from '@/lib/firebase';
import { useBusiness } from '@/lib/business';
import { useAuth } from '@/lib/auth';
import { formatPula } from '@/lib/money';
import type { Booking, BookingStatus } from '@/lib/types';
import { statusBadge } from './ui';

const FILTERS: (BookingStatus | 'all')[] = ['all', 'new', 'confirmed', 'completed', 'cancelled'];

export function Bookings() {
  const biz = useBusiness();
  const { role } = useAuth();
  const showTotals = role !== 'staff';
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [filter, setFilter] = useState<BookingStatus | 'all'>('all');
  const [selected, setSelected] = useState<Booking | null>(null);
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState('');
  // Editable appointment date/time (adjust after calling the client, before confirming).
  const [editDate, setEditDate] = useState('');
  const [editTime, setEditTime] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(false);

  useEffect(() => {
    setEditDate(selected?.date || '');
    setEditTime(selected?.time || '');
    setConfirmDelete(false);
  }, [selected?.id]); // eslint-disable-line

  useEffect(() => {
    const q = query(collection(db, 'bookings'), orderBy('createdAt', 'desc'));
    return onSnapshot(
      q,
      (snap) => setBookings(snap.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<Booking, 'id'>) }))),
      () => setBookings([]),
    );
  }, []);

  const shown = useMemo(
    () => (filter === 'all' ? bookings : bookings.filter((b) => b.status === filter)),
    [bookings, filter],
  );

  function flash(msg: string) {
    setToast(msg);
    setTimeout(() => setToast(''), 3500);
  }

  async function setStatus(b: Booking, status: BookingStatus) {
    setBusy(true);
    try {
      await updateDoc(doc(db, 'bookings', b.id), {
        status,
        updatedAt: new Date().toISOString(),
        ...(status === 'confirmed' ? { confirmedAt: new Date().toISOString() } : {}),
      });
      setSelected((s) => (s && s.id === b.id ? { ...s, status } : s));
      flash(`Marked ${status}.`);
    } catch (e) {
      flash(e instanceof Error ? e.message : 'Update failed.');
    } finally {
      setBusy(false);
    }
  }

  async function saveDateTime() {
    if (!selected) return;
    setBusy(true);
    try {
      await updateDoc(doc(db, 'bookings', selected.id), {
        date: editDate, time: editTime, updatedAt: new Date().toISOString(),
      });
      setSelected((s) => (s ? { ...s, date: editDate, time: editTime } : s));
      flash('Appointment date & time updated.');
    } catch (e) {
      flash(e instanceof Error ? e.message : 'Update failed.');
    } finally {
      setBusy(false);
    }
  }

  async function remove(b: Booking) {
    setBusy(true);
    try {
      await deleteDoc(doc(db, 'bookings', b.id));
      setSelected(null);
      flash('Booking deleted.');
    } catch (e) {
      flash(e instanceof Error ? e.message : 'Delete failed.');
    } finally {
      setBusy(false);
    }
  }

  async function confirmAndEmail(b: Booking) {
    setBusy(true);
    try {
      // Confirm with the (possibly adjusted) date/time.
      await updateDoc(doc(db, 'bookings', b.id), {
        status: 'confirmed',
        date: editDate, time: editTime,
        confirmedAt: new Date().toISOString(),
        confirmationEmailSentAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      });
      const lines = b.selectedServices.map((s) => `• ${s.name} — ${formatPula(s.price)}`).join('\n');
      const when = editDate ? `\n\nYour appointment: ${editDate} ${editTime || ''}` : '';
      const body =
        `Dear ${b.fullName.split(' ')[0]},\n\n` +
        `Wonderful news — your booking at ${biz.name} is confirmed.\n\n` +
        `${lines}\n\nEstimated total: ${formatPula(b.totalPrice)}${when}\n\n` +
        `Please arrive 10 minutes early. To make any changes, just reply to this email or call ${biz.phone}.\n\n` +
        `We look forward to welcoming you.\nRelax. Refresh. Rejuvenate.`;
      await sendGraceHavenEmail({
        to: b.email,
        subject: `Your booking is confirmed — ${biz.name}`,
        body,
        relatedEntityType: 'booking',
        relatedEntityId: b.id,
      });
      setSelected((s) => (s && s.id === b.id ? { ...s, status: 'confirmed', date: editDate, time: editTime } : s));
      flash('Confirmed and emailed the guest.');
    } catch (e) {
      flash(e instanceof Error ? e.message : 'Could not send. Check email config.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div>
      <h1 className="font-display text-3xl text-espresso">Bookings</h1>
      <p className="mt-1 font-body text-sm text-muted">Confirm requests and notify guests by email.</p>

      <div className="mt-6 flex flex-wrap gap-2">
        {FILTERS.map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className={`chip ${filter === f ? 'border-gold bg-gold/15 text-espresso' : 'border-line bg-white text-muted hover:border-gold'}`}
          >
            {f}
          </button>
        ))}
      </div>

      <div className="mt-5 overflow-x-auto border border-line bg-paper">
        <table className="w-full min-w-[640px] text-left">
          <thead>
            <tr className="border-b border-line font-body text-[11px] uppercase tracking-wide text-muted">
              <th className="px-5 py-3">Guest</th><th className="px-5 py-3">Treatments</th>
              <th className="px-5 py-3">Preferred</th>{showTotals && <th className="px-5 py-3">Total</th>}
              <th className="px-5 py-3">Status</th><th className="px-5 py-3" />
            </tr>
          </thead>
          <tbody>
            {shown.map((b) => (
              <tr key={b.id} className="border-b border-line last:border-0 hover:bg-cream/40">
                <td className="px-5 py-3.5">
                  <p className="font-body text-sm font-semibold text-espresso">{b.fullName}</p>
                  <p className="font-body text-xs text-muted">{b.email}</p>
                </td>
                <td className="px-5 py-3.5 font-body text-sm text-muted">{b.selectedServices?.length} item(s)</td>
                <td className="px-5 py-3.5 font-body text-sm text-muted">{b.date || '—'} {b.time || ''}</td>
                {showTotals && <td className="px-5 py-3.5 font-display text-base text-espresso">{formatPula(b.totalPrice)}</td>}
                <td className="px-5 py-3.5">{statusBadge(b.status)}</td>
                <td className="px-5 py-3.5 text-right">
                  <button onClick={() => setSelected(b)} className="font-body text-sm text-gold-deep hover:underline">Open</button>
                </td>
              </tr>
            ))}
            {shown.length === 0 && (
              <tr><td colSpan={showTotals ? 6 : 5} className="px-5 py-10 text-center font-body text-sm text-muted">No bookings here.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Detail drawer */}
      {selected && (
        <div className="fixed inset-0 z-50 flex justify-end bg-black/40" onClick={() => setSelected(null)}>
          <div className="h-full w-full max-w-md overflow-y-auto bg-cream p-7 shadow-soft" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between">
              <div>
                <h2 className="font-display text-2xl text-espresso">{selected.fullName}</h2>
                <div className="mt-2">{statusBadge(selected.status)}</div>
              </div>
              <button onClick={() => setSelected(null)} className="text-muted hover:text-espresso"><X size={22} /></button>
            </div>

            <div className="mt-6 space-y-2.5 border-y border-line py-5 font-body text-sm text-muted">
              <p className="flex items-center gap-2"><Mail size={15} className="text-gold-deep" /> {selected.email}</p>
              {selected.phone && <p className="flex items-center gap-2"><Phone size={15} className="text-gold-deep" /> {selected.phone}</p>}
              <p className="flex items-center gap-2"><Clock size={15} className="text-gold-deep" /> ~{selected.totalMin} min</p>
            </div>

            {/* Adjustable appointment date & time — set after discussing with the guest, then confirm. */}
            <p className="eyebrow mt-5 mb-2">Appointment date &amp; time</p>
            <div className="flex gap-2.5">
              <input type="date" className="field !py-2 flex-1" value={editDate} onChange={(e) => setEditDate(e.target.value)} />
              <input type="text" className="field !py-2 w-28" value={editTime} onChange={(e) => setEditTime(e.target.value)} placeholder="e.g. 14:00" />
            </div>
            <button onClick={saveDateTime} disabled={busy} className="mt-2 inline-flex items-center gap-1.5 font-body text-sm text-gold-deep hover:underline disabled:opacity-40">
              <Save size={14} /> Save date &amp; time
            </button>

            <p className="eyebrow mt-5 mb-2">Treatments</p>
            <div className="divide-y divide-line border-y border-line">
              {selected.selectedServices.map((s, i) => (
                <div key={i} className="flex justify-between py-2.5 font-body text-sm">
                  <span className="text-espresso">{s.name}</span>{showTotals && <span className="text-gold-deep">{formatPula(s.price)}</span>}
                </div>
              ))}
            </div>
            {showTotals && (
              <div className="mt-3 flex justify-between font-display text-lg text-espresso">
                <span>Total</span><span>{formatPula(selected.totalPrice)}</span>
              </div>
            )}

            {selected.specialRequests && (
              <>
                <p className="eyebrow mt-5 mb-2">Notes</p>
                <p className="bg-paper p-3 font-body text-sm text-muted">{selected.specialRequests}</p>
              </>
            )}

            <div className="mt-7 space-y-2.5">
              <button onClick={() => confirmAndEmail(selected)} disabled={busy} className="btn-gold w-full">
                <Check size={16} /> Confirm &amp; email guest
              </button>
              <div className="flex gap-2.5">
                <button onClick={() => setStatus(selected, 'completed')} disabled={busy} className="btn-outline flex-1 !px-4">Completed</button>
                <button onClick={() => setStatus(selected, 'cancelled')} disabled={busy} className="btn-outline flex-1 !px-4 !border-blush-deep !text-blush-deep hover:!bg-blush-deep hover:!text-cream">Void / cancel</button>
              </div>
            </div>
            {selected.confirmationEmailSentAt && (
              <p className="mt-4 text-center font-body text-xs text-muted">Confirmation emailed ✓</p>
            )}

            {/* Permanent delete (e.g. test bookings) */}
            {confirmDelete ? (
              <div className="mt-5 border border-blush-deep/40 bg-blush-soft/40 p-3">
                <p className="font-body text-sm text-espresso">Permanently delete this booking? This can't be undone.</p>
                <div className="mt-2.5 flex gap-2.5">
                  <button onClick={() => remove(selected)} disabled={busy} className="btn-outline flex-1 justify-center !border-blush-deep !text-blush-deep hover:!bg-blush-deep hover:!text-cream">Yes, delete</button>
                  <button onClick={() => setConfirmDelete(false)} className="btn-outline flex-1 justify-center">Keep</button>
                </div>
              </div>
            ) : (
              <button onClick={() => setConfirmDelete(true)} className="mt-5 inline-flex items-center gap-1.5 font-body text-xs text-blush-deep hover:underline">
                <Trash2 size={13} /> Delete booking permanently
              </button>
            )}
          </div>
        </div>
      )}

      {toast && (
        <div className="fixed bottom-6 left-1/2 z-50 -translate-x-1/2 bg-espresso px-5 py-3 font-body text-sm text-cream shadow-soft">
          {toast}
        </div>
      )}
    </div>
  );
}
